import { Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { CHAT_EDITOR_FOCUS_MAX_CLASS } from '@/lib/chat-focus-layout'

export type ChatFirstHomeProps = {
  userName?: string
  /** compact = agent panel; comfortable = editor tab / wide surface */
  variant?: 'compact' | 'comfortable'
  /** Centered hero composer. Omit when the pane docks one composer at the bottom. */
  composer?: ReactNode
  /** Optional node rendered above the composer (e.g. document context chip) */
  contextNode?: ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * Chat-First Home empty state (from ?test=chat-window prototype).
 * Welcome headline + large centered composer. Same layout in agent panel and editor tab.
 */
export function ChatFirstHome({
  userName = 'there',
  variant = 'comfortable',
  composer,
  contextNode,
  className,
  'data-testid': dataTestId,
}: ChatFirstHomeProps) {
  const compact = variant === 'compact'
  const displayName = userName.trim() || 'there'

  return (
    <div
      className={cn(
        'flex flex-1 flex-col items-center justify-center',
        compact ? 'px-3 py-4' : 'px-4 py-8',
        className,
      )}
      data-testid={dataTestId ?? testIds.chatFirstHome.root}
    >
      <div
        className={cn(
          'flex w-full flex-col items-center',
          compact ? 'max-w-none' : CHAT_EDITOR_FOCUS_MAX_CLASS,
        )}
      >
        <div className={cn('text-center', compact ? 'mb-5' : 'mb-8')}>
          <h1
            className={cn(
              'inline-flex items-center gap-2 font-normal text-muted-foreground',
              compact ? 'text-lg' : 'text-3xl',
            )}
          >
            <Sparkles
              className={cn('text-primary/70', compact ? 'h-4 w-4' : 'h-7 w-7')}
              aria-hidden
            />
            <span>Welcome back, {displayName}</span>
          </h1>
        </div>

        {composer || contextNode ? (
          <div className="w-full" data-testid={testIds.chatFirstHome.composerSlot}>
            {contextNode && <div className={cn('mb-3', compact ? 'text-xs' : 'text-sm')}>{contextNode}</div>}
            {composer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** @deprecated Use ChatFirstHome */
export const ChatNewConversationWelcome = ChatFirstHome
export type ChatNewConversationWelcomeProps = ChatFirstHomeProps

/** @deprecated Use ChatFirstHome */
export function ChatDefaultState(_props: { onSuggestedPrompt: (prompt: string) => void }) {
  return null
}
